/**
 * Chummys product page — assets/chummys-pdp.js
 *   1. Box size cards drive the theme's hidden variant radios.
 *   2. The delivery day picker: works out the days we can actually land on,
 *      then writes the choice to the line item and to Zapiet's cart attribute.
 */
(() => {
  const LONDON = 'Europe/London';
  const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  /* ---------------- 1. box size cards ---------------- */
  const wireSizes = () => {
    const box = document.querySelector('[data-chummys-sizes]');
    if (!box) return;
    document.body.classList.add('chummys-pdp-cards');
    const picker = document.querySelector('brownie-options');
    if (picker && !picker.id) picker.id = 'build-your-box';
    const radios = () => [...document.querySelectorAll('variant-picker input[type="radio"]')];
    box.addEventListener('click', e => {
      const card = e.target.closest('.chummys-pdp-size');
      if (!card) return;
      const want = card.dataset.optionValue;
      const radio = radios().find(r => (r.dataset.optionValue || r.value) === want);
      if (radio && !radio.checked) { radio.checked = true; radio.dispatchEvent(new Event('change', { bubbles: true })); }
      box.querySelectorAll('.chummys-pdp-size').forEach(c => c.setAttribute('aria-pressed', String(c === card)));
    });
    // the theme can change the variant on its own (deep link, back button)
    document.addEventListener('change', e => {
      if (!e.target.closest('variant-picker')) return;
      const val = e.target.dataset?.optionValue || e.target.value;
      box.querySelectorAll('.chummys-pdp-size').forEach(c => c.setAttribute('aria-pressed', String(c.dataset.optionValue === val)));
    });
  };

  /* ---------------- 2. the day it lands ---------------- */
  const londonParts = d => {
    const f = new Intl.DateTimeFormat('en-GB', { timeZone: LONDON, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', weekday: 'short', hour12: false });
    const p = Object.fromEntries(f.formatToParts(d).map(x => [x.type, x.value]));
    return { y: +p.year, m: +p.month, d: +p.day, hh: +p.hour % 24, mm: +p.minute, dow: DOW.indexOf(p.weekday) };
  };
  const addDays = (y, m, d, n) => { const t = new Date(Date.UTC(y, m - 1, d + n)); return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate(), dow: t.getUTCDay() }; };
  const iso = o => `${o.y}-${String(o.m).padStart(2, '0')}-${String(o.d).padStart(2, '0')}`;
  const uk = o => `${String(o.d).padStart(2, '0')}/${String(o.m).padStart(2, '0')}/${o.y}`;
  const nice = o => `${DOW[o.dow]} ${o.d} ${MON[o.m - 1]}`;

  /* the landing-day picker now lives in chummys-plan.js (<chummys-plan>, still id="landing-day") */
  /* ---------------- 3. the flavour picker, without touching the picker ----------------
     <brownie-options> is fulfilment-critical: it serialises the box into one
     line-item property in an exact format the kitchen reads. So nothing here
     writes that property. Every action below clicks the component's own chips
     and cells, which means the box is filled by exactly the same code path a
     customer's finger uses, and the property can't drift.

     What it adds: the box is never empty (an empty box blocks the sale — the
     picker fails closed under its minimum), plus one-tap ways to fill it. */
  const BAKERS = ['Original Recipe', 'Salted Caramel', 'Lotus Biscuit', 'Kinder Bueno',
    'Triple Chocolate', 'Oreo', 'Nutella', 'Ferrero Rocher',
    'White Chocolate & Raspberry', 'Mars', 'Snickers', 'Chocolate Orange'];

  const enhancePicker = el => {
    if (!el || el.dataset.chummysEnhanced) return;
    el.dataset.chummysEnhanced = '1';

    const chips = () => [...el.querySelectorAll('[data-flavour-chip]')];
    const box = el.querySelector('[data-box]');
    const totalEl = el.querySelector('[data-total]');
    const maxEl = el.querySelector('[data-max]');
    const total = () => Number(totalEl?.textContent || 0);
    const max = () => Number(maxEl?.textContent || 0);
    const chipFor = name => chips().find(c => c.dataset.flavour === name);

    const clear = () => {
      for (let guard = 0; guard < 60; guard++) {
        const filled = box?.querySelector('.brownie-options__cell--filled:not([disabled])');
        if (!filled) break;
        filled.click();
      }
    };
    const fill = names => {
      const cap = max();
      for (let i = 0; i < names.length * 3 && total() < cap; i++) {
        const chip = chipFor(names[i % names.length]);
        if (!chip || chip.disabled) continue;
        chip.click();
      }
    };
    const shuffled = () => {
      const names = chips().map(c => c.dataset.flavour);
      for (let i = names.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [names[i], names[j]] = [names[j], names[i]]; }
      return names;
    };

    // actions, dropped in under the picker's own header
    // the bar and note are in the HTML now (no layout shift); build them only if a template lacks them
    let bar = el.querySelector('[data-pick-actions]');
    if (!bar) {
      bar = document.createElement('div');
      bar.className = 'chummys-pick__actions';
      bar.innerHTML = `
      <button type="button" class="chummys-pick__act" data-act="bakers">Baker's pick</button>
      <button type="button" class="chummys-pick__act" data-act="random">Surprise me</button>
      <button type="button" class="chummys-pick__act chummys-pick__act--quiet" data-act="clear">Start again</button>`;
      const header = el.querySelector('.brownie-options__header');
      (header || el).insertAdjacentElement(header ? 'afterend' : 'afterbegin', bar);
    }
    let note = el.querySelector('[data-pick-note]');
    if (!note) { note = document.createElement('p'); note.className = 'chummys-pick__note'; bar.insertAdjacentElement('afterend', note); }

    // the note keeps its line so the box below never jumps
    const say = txt => { note.textContent = txt || '\u00a0'; note.classList.toggle('is-quiet', !txt); };
    say('');

    bar.addEventListener('click', e => {
      const b = e.target.closest('[data-act]');
      if (!b) return;
      const act = b.dataset.act;
      if (act === 'clear') { clear(); say('Empty box — pick as many of each as you like.'); return; }
      clear();
      fill(act === 'bakers' ? BAKERS : shuffled());
      say(act === 'bakers' ? 'Our bestsellers, in the box. Tap any one to swap it out.'
                           : 'A random pick. Tap any one to swap it out.');
    });

    // anything the customer does themselves retires the note
    chips().forEach(c => c.addEventListener('click', () => say(''), { once: true }));
    box?.addEventListener('click', () => say(''), { once: true });

    // never leave the box empty: an empty box cannot be bought
    const seed = () => {
      if (total() > 0 || max() <= 0) return;
      if (/[?&]box=/.test(location.search)) return; // packed on the collection page's counter
      fill(BAKERS);
      if (total() > 0) { say("We've started you off with our bestsellers — tap any one to swap it out."); return; }
      // a range whose flavours are not on the bestseller list (blondies, cookies): fill in menu order
      fill(chips().map(c => c.dataset.flavour));
      if (total() > 0) say("We've started you off with a mix — tap any one to swap it out.");
    };
    // photographs: each filled cell gets the real square; the drawing stays as the fallback for a flavour without one
    let photos = {};
    try { photos = JSON.parse(el.querySelector('[data-brownie-photos]')?.textContent || '{}'); } catch (e) { photos = {}; }
    const dress = () => {
      el.querySelectorAll('.brownie-options__cell--filled').forEach(cell => {
        const img = cell.querySelector('img[data-photo]');
        const src = photos[cell.dataset.flavour];
        if (!img || !src || img.getAttribute('src')) return;
        img.alt = cell.dataset.flavour || '';
        img.addEventListener('load', () => cell.classList.add('has-photo'), { once: true });
        img.addEventListener('error', () => img.remove(), { once: true });
        img.src = src;
      });
    };
    dress();
    if (box) new MutationObserver(dress).observe(box, { childList: true, subtree: true });

    setTimeout(seed, 60);
    el.closest('form')?.addEventListener('variant:change', () => setTimeout(seed, 120));
  };

  const pickerReady = () => {
    const el = document.querySelector('brownie-options');
    if (!el) return;
    if (window.customElements?.whenDefined) customElements.whenDefined('brownie-options').then(() => setTimeout(() => enhancePicker(el), 40));
    else enhancePicker(el);
  };
  /* The collection page's counter sends people here with ?box=handle,handle — drop those squares in the box. */
  const fromCounter = () => {
    let want = [];
    try { want = (new URLSearchParams(location.search).get('box') || '').split(',').filter(Boolean); } catch (e) { return; }
    if (!want.length) return;
    const key = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    const tryFill = (n) => {
      const chips = [...document.querySelectorAll('[data-flavour-chip]')];
      if (!chips.length || !customElements.get('brownie-options')) { if (n < 60) setTimeout(() => tryFill(n + 1), 150); return; }
      const box = document.querySelector('brownie-options [data-box]');
      for (let g = 0; g < 60; g++) { const filled = box && box.querySelector('.brownie-options__cell--filled:not([disabled])'); if (!filled) break; filled.click(); }
      want.forEach((h, i) => { const chip = chips.find((c) => key(c.dataset.flavour) === h); if (chip) setTimeout(() => chip.click(), 90 * i); });
      setTimeout(() => { const box = document.getElementById('build-your-box'); if (box) box.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 90 * want.length + 250);
    };
    setTimeout(() => tryFill(0), 250);
  };
  const boot = () => { wireSizes(); pickerReady(); fromCounter(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();

/* sticky-variant-clean: the theme rewrites the sticky buy bar's variant line with the raw variant title on
   every variant change ("Box of 12 / Normal"). "Normal" is the store's default Packaging value and means
   nothing to a customer, so strip it whenever the line changes. Occasion values ("Get Well Soon") stay. */
(function () {
  var el = document.querySelector('[data-sticky-product-options]');
  if (!el) return;
  var clean = function () {
    var t = el.textContent;
    var c = t.replace(/\s*\/\s*Normal\b/, '').replace(/^\s*Normal\s*\/\s*/, '');
    if (c !== t) el.textContent = c;
  };
  clean();
  new MutationObserver(clean).observe(el, { childList: true, characterData: true, subtree: true });
})();
